import { EAnimalTypes, EAnimalTypesUI } from './animals.types';
import { TOptionType, TOptionTypeUi } from './general.types';

export enum EAnimalInfoConfig {
  ANIMAL_PHOTO = 'ANIMAL_PHOTO',
  ANIMAL_BIRTHDAY = 'ANIMAL_BIRTHDAY',
  ANIMAL_SPECIES = 'ANIMAL_SPECIES',
  ANIMAL_BREED = 'ANIMAL_BREED',
  ANIMAL_GENDER = 'ANIMAL_GENDER',
  ANIMAL_COLOR = 'ANIMAL_COLOR',
  ANIMAL_WEIGHT = 'ANIMAL_WEIGHT',
  MICROCHIP_NUMBER = 'MICROCHIP_NUMBER',
  SPAYED_NEUTERED = 'SPAYED_NEUTERED',
  HOUSE_TRAINED = 'HOUSE_TRAINED',
  HISTORY_OF_AGGRESSION = 'HISTORY_OF_AGGRESSION',
}

/**
 * What a config set says about one Basic Animal Information field.
 *
 * Only REQUIRED and HIDDEN are transmitted — as two lists of criteria. A field in neither list is
 * OPTIONAL, which is what an unchecked box has always meant, so sets saved before this existed read
 * back unchanged.
 */
export enum EAnimalInfoVisibility {
  REQUIRED = 'REQUIRED',
  OPTIONAL = 'OPTIONAL',
  HIDDEN = 'HIDDEN',
}

export type TPropertyConfigNoteDB = {
  id?: string;
  title: string | null;
  description: string;
};
export type TPropertyConfigNoteUI = {
  id?: string;
  title: string;
  description: string;
};

export type TPropertyConfigEditNoteUI = {
  animalRequirementsSetId: string;
  note: TPropertyConfigNoteUI;
  name?: string;
  propertyId?: string;
};

export type TPropertyConfigEditNoteDB = {
  animalVerificationConfigurationId: string;
  note: TPropertyConfigNoteDB;
  newConfigurationSetName?: string;
  propertyId?: string;
};

export type TPropertyWithAccessibilityInfoDB = {
  id: string;
  name: string;
  beneficiaryUserHasAccessToProperty: boolean;
};
export type TPropertyWithAccessibilityInfoUI = {
  id: string;
  name: string;
  beneficiaryUserHasAccessToProperty: boolean;
};

export type TPropertyConfigSetPetRestrictionsUI = {
  breeds: TOptionTypeUi[] | null;
  species: TOptionTypeUi[] | null;
  maxWeightLimit?: number | null;
  maxNumberOfPets?: number | null;
  isPetAllowed?: string;
};
// TODO: align with BE
export type TPropertyConfigSetPetRestrictionsDB = {
  restrictedBreeds: TOptionType[] | null;
  allowedSpecies: TOptionType[] | null;
  maxPetWeight: number | null;
  maxNumOfPetsAllowed: number | null;
  noPetPolicy?: boolean;
};

// TODO: align with BE
export enum ERequiredDocument {
  RABIES_VACCINATION = 'RABIES',
  CITY_LICENSE = 'CITY_LICENSING',
  SPAYED_NEUTERED_DOCUMENT = 'SPAYED_NEUTERED',
  DOG_BITE_LIABILITY_INSURANCE = 'DOG_LIABILITY_INSURANCE',
  PARVO = 'PARVO',
}

export type TSpeciesRequirementUI = {
  dog: boolean;
  cat: boolean;
};
// TODO: align with BE
export enum ESpeciesRequirementDB {
  DOG = 'DOG',
  CAT = 'CAT',
}

export type TRequiredDocumentsUI = {
  [key in ERequiredDocument]?: boolean | TSpeciesRequirementUI;
};

export type TCustomRequiredDocumentUI = {
  id: string; // generated in FE for editing/deleting
  name: string;
  hasExpirationDate: 'yes' | 'no';
  requiredSpecies: TSpeciesRequirementUI;
  requiredAnimalTypes: EAnimalTypesUI[];
};

export type TRequiredVetDocumentsUI = {
  expirable: boolean;
  name: string;
  applicableSpecies: TSpeciesRequirementUI;
  requiredAnimalTypes?: EAnimalTypesUI[];
  predefined: boolean;
};

export type TRequiredDocumentsDB = {
  expirable: boolean;
  name: string;
  applicableSpecies: ESpeciesRequirementDB[];

  applicableAnimalTypes?: EAnimalTypesUI[];
  predefined: boolean;
};
// TODO: align with BE
export type TCustomRequiredDocumentDB = {
  id: string;
  name: string;
  hasExpirationDate: boolean;
  requiredSpecies: ESpeciesRequirementDB[] | null;
  requiredAnimalTypes: EAnimalTypes[] | null;
};

export type TPropertyConfigSetGetUI = {
  id: string;
  name: string;
  requirements: EAnimalInfoConfig[];
  hiddenRequirements: EAnimalInfoConfig[];
  notes: TPropertyConfigNoteUI[];
  isDefault?: boolean;
  properties?: TPropertyWithAccessibilityInfoUI[];
  tenantShouldPayForPet: boolean;
  petRestrictions?: TPropertyConfigSetPetRestrictionsUI;
  requiredDocuments?: TRequiredDocumentsUI;
  customRequiredDocuments?: TCustomRequiredDocumentUI[];
  reVerifyKnownAssistanceAnimals: boolean;
  isCourseRequired: boolean;
  hideDocumentsSection: boolean;
};

export type TPropertyConfigSetGetDB = {
  id: string;
  name: string;
  requirements: EAnimalInfoConfig[];
  hiddenRequirements: EAnimalInfoConfig[] | null;
  notes: TPropertyConfigNoteDB[];
  isDefault?: boolean;
  properties?: TPropertyWithAccessibilityInfoDB[];
  tenantShouldPayForPet: boolean;
  petRestrictions: TPropertyConfigSetPetRestrictionsDB | null;
  requiredVetRecords: TRequiredDocumentsDB[] | null;
  reVerifyKnownAssistanceAnimals?: boolean;
  isAnimalCurriculumRequired?: boolean;
  hideDocumentsSection?: boolean;
};

export type TPropertyConfigSetCreateUI = {
  propertyIds: string[];
  notes?: TPropertyConfigNoteUI[];
  animalRequirements: EAnimalInfoConfig[];
  hiddenAnimalRequirements: EAnimalInfoConfig[];
  name: string;
  requiredDocuments: TCustomRequiredDocumentUI[];
  isPetAllowed: string;
  maxPets: number;
  maxPetWeight: number;
  reVerifyKnownAssistanceAnimals?: boolean;
  isCourseRequired?: boolean;
  hideDocumentsSection?: boolean;
};

export type TPropertyConfigSetCreateDB = {
  propertyIds: string[];
  notes?: TPropertyConfigNoteDB[];
  animalVerificationCriteria: EAnimalInfoConfig[];
  hiddenAnimalVerificationCriteria: EAnimalInfoConfig[];
  name: string;
  noPetPolicy?: boolean;
  petRestrictions: TPropertyConfigSetPetRestrictionsDB | null;
  requiredVetRecords: TRequiredDocumentsDB[];
  reVerifyKnownAssistanceAnimals: boolean;
  isAnimalCurriculumRequired: boolean;
  hideDocumentsSection: boolean;
};

export type TCustomRequiredDocumentsUI = {
  hasExpirationDate: 'yes' | 'no';
  id: string;
  name: string;
  requiredAnimalTypes: string[];
  requiredSpecies: boolean | TSpeciesRequirementUI;
};

export type TPropertyConfigSetEditUI = {
  id: string;
  requirements: EAnimalInfoConfig[];
  hiddenRequirements: EAnimalInfoConfig[];
  name: string;
  newAssignedPropertyIds?: string[];
  unassignedPropertyIds?: string[];
  replacingAnimalVerificationConfigurationId?: string;

  isPetAllowed: 'no' | 'yes';
  maxPets?: number;
  maxPetWeight?: number;
  restrictedBreeds?: TOptionTypeUi[];
  species?: TOptionTypeUi[];
  requiredDocuments: TRequiredDocumentsUI;
  customRequiredDocuments: TCustomRequiredDocumentsUI[];
  reVerifyKnownAssistanceAnimals?: boolean;
  isCourseRequired?: boolean;
  hideDocumentsSection?: boolean;
};
export type TPropertyConfigSetEditDB = {
  animalVerificationConfigurationId: string;
  criteria: EAnimalInfoConfig[];
  hiddenCriteria: EAnimalInfoConfig[];
  name: string;
  replacingAnimalVerificationConfigurationId?: string;
  newlyAssignedProperties?: string[];
  newlyUnassignedProperties?: string[];
  // TODO: add petRestrictions
  noPetPolicy: boolean;
  petRestrictions?: {
    maxNumOfPetsAllowed?: number;
    maxPetWeight?: number;
    restrictedBreeds?: string[];
    allowedSpecies?: string[];
  };
  requiredVetRecords: TRequiredDocumentsDB[];
  reVerifyKnownAssistanceAnimals: boolean;
  isAnimalCurriculumRequired: boolean;
  hideDocumentsSection: boolean;
};

export enum EConfigSetChangeActionTypeDB {
  EDITED_ASSIGNED_PROPERTIES = 'EDITED_ASSIGNED_PROPERTIES',
  EDITED_RESTRICTED_BREEDS = 'EDITED_RESTRICTED_BREEDS',
  EDITED_ALLOWED_SPECIES = 'EDITED_ALLOWED_SPECIES',
  UPDATED_MAX_PET_WEIGHT = 'UPDATED_MAX_PET_WEIGHT',
  UPDATED_MAX_PETS_PER_UNIT = 'UPDATED_MAX_PETS_PER_UNIT',
  ADDED_NOTE = 'ADDED_NOTE',
  EDITED_NOTE = 'EDITED_NOTE',
  DELETED_NOTE = 'DELETED_NOTE',
  EDITED_BASIC_ANIMAL_INFORMATION = 'EDITED_BASIC_ANIMAL_INFORMATION',
  ADDED_DOCUMENT_REQUIREMENT = 'ADDED_DOCUMENT_REQUIREMENT',
  UPDATED_DOCUMENT_REQUIREMENT = 'UPDATED_DOCUMENT_REQUIREMENT',
  REMOVED_DOCUMENT_REQUIREMENT = 'REMOVED_DOCUMENT_REQUIREMENT',
  EDITED_DOCUMENTS_SECTION_VISIBILITY = 'EDITED_DOCUMENTS_SECTION_VISIBILITY',
  EDITED_ANIMAL_CURRICULUM_REQUIREMENT = 'EDITED_ANIMAL_CURRICULUM_REQUIREMENT',
}

export type TConfigSetChangeDB = {
  actionType: EConfigSetChangeActionTypeDB;
  documentName: string | null;
};

export type TConfigSetChangeLogGroupDB = {
  saveGroupId: string;
  occurredOn: string;
  userName: string | null;
  userRole: string | null;
  changes: TConfigSetChangeDB[];
};

export type TConfigSetChangeLogGroupUI = {
  saveGroupId: string;
  occurredOn: string;
  userName: string;
  userRole: string;
  changes: string[];
};
